/**
 * V57.2: 평가서 관리 (템플릿 마스터 폼빌더)
 *
 * /hr/evaluation-templates
 * 권한: evaluation.manage (ADMIN_LIKE + role_manager)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Plus, ChevronDown, ChevronRight, Trash2, ArrowUp, ArrowDown, Eye, Edit2, Save } from 'lucide-react';
import {
  useHrEvaluations,
  EvaluationTemplateItem,
  EvaluationNode,
  EvaluationItemV2,
  ScoringScale,
  EvaluationType,
  EVALUATION_TYPE_LABELS,
  EvaluationRole,
  TemplateStatus,
} from '../../shared/hooks/useEvaluations';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { CompanyCode } from '../../shared/types/employee';
import { COMPANY_CODE_OPTIONS } from '../../shared/constants/company';

// ============================================
// 헬퍼
// ============================================

function generateId(): string {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

function getNodeDepth(nodeId: string, nodesMap: Map<string, EvaluationNode>): number {
  let depth = 0;
  let current = nodesMap.get(nodeId);
  while (current && current.parentId !== null) {
    depth++;
    current = nodesMap.get(current.parentId);
    if (depth > 10) break;
  }
  return depth;
}

function getChildren(parentId: string | null, nodes: EvaluationNode[]): EvaluationNode[] {
  return nodes.filter(n => n.parentId === parentId).sort((a, b) => a.order - b.order);
}

function getNodeItems(nodeId: string, items: EvaluationItemV2[]): EvaluationItemV2[] {
  return items.filter(it => it.nodeId === nodeId).sort((a, b) => a.order - b.order);
}

function findTopAncestorId(nodeId: string, nodesMap: Map<string, EvaluationNode>): string {
  let current = nodesMap.get(nodeId);
  if (!current) return nodeId;
  while (current.parentId !== null) {
    const parent = nodesMap.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return current.id;
}

// ============================================
// 메인 페이지
// ============================================

export default function HrEvaluationTemplatesPage() {
  const { hasPermission, hasAnyRole } = useAuthorization();
  const navigate = useNavigate();

  // V57.3: 권한 가드 SSoT 통일 (HrPerformancePage 패턴 따름)
  // auth_roles에 evaluation.manage가 미반영된 경우 역할 기반 폴백
  const canAccess = hasPermission('evaluation.manage')
    || hasAnyRole(['role_hr', 'system_admin', 'role_admin_kr', 'role_admin_cn', 'role_manager']);

  const {
    templates,
    loading,
    error,
    listEvaluationTemplates,
    createEvaluationTemplate,
    updateEvaluationTemplate,
    seedSystemEvaluationTemplates,
  } = useHrEvaluations();

  const [selectedTemplate, setSelectedTemplate] = useState<EvaluationTemplateItem | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'editor' | 'preview'>('info');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState<string>('');
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);
  // V57.6: 생성 후 자동 선택용 pending ID
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  // V57.6.1: system_admin 전용 회사 선택
  const isSystemAdmin = hasAnyRole(['system_admin']);
  const [editCompanyCode, setEditCompanyCode] = useState<CompanyCode | ''>('');

  // V57.7: 시스템 고정 템플릿 잠금 판별
  const isTemplateLocked = !!(selectedTemplate?.isLocked || selectedTemplate?.isSystemTemplate);

  // 편집 상태
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState<EvaluationType>('annual');
  const [editMaxDepth, setEditMaxDepth] = useState<1 | 2 | 3>(2);
  const [editScale, setEditScale] = useState<ScoringScale>({ min: 1, max: 5, step: 1 });
  const [editNodes, setEditNodes] = useState<EvaluationNode[]>([]);
  const [editItems, setEditItems] = useState<EvaluationItemV2[]>([]);

  useEffect(() => {
    listEvaluationTemplates({
      activeOnly: filterActiveOnly || undefined,
      type: (filterType as EvaluationType) || undefined,
    });
  }, [listEvaluationTemplates, filterType, filterActiveOnly]);

  const filteredTemplates = useMemo(() => {
    return templates;
  }, [templates]);

  const handleSelectTemplate = useCallback((tpl: EvaluationTemplateItem) => {
    setSelectedTemplate(tpl);
    setEditTitle(tpl.title);
    setEditType(tpl.type);
    setEditMaxDepth(tpl.maxDepth || 2);
    setEditScale(tpl.scoringScale || { min: 1, max: 5, step: 1 });
    setEditNodes(tpl.nodes || []);
    setEditItems(tpl.items || []);
    setActiveTab('info');
  }, []);

  // V57.6: 생성 후 자동 선택 effect
  useEffect(() => {
    if (pendingSelectId && templates.length > 0) {
      const created = templates.find(t => t.id === pendingSelectId);
      if (created) {
        handleSelectTemplate(created);
        setActiveTab('editor');
        setPendingSelectId(null);
      }
    }
  }, [pendingSelectId, templates, handleSelectTemplate]);

  // ============================================
  // 새 템플릿 생성
  // ============================================

  const handleCreate = useCallback(async () => {
    if (!editTitle.trim()) return;
    if (isSystemAdmin && !editCompanyCode) return;
    setSaving(true);
    try {
      // V57.6: draft로 생성 (항목 0개 허용). 기본 메타만 전달.
      const id = await createEvaluationTemplate({
        type: editType,
        title: editTitle.trim(),
        maxDepth: editMaxDepth,
        scoringScale: editScale,
        scoringMethod: 'weighted_sum',
        ...(isSystemAdmin && editCompanyCode ? { companyCode: editCompanyCode } : {}),
      });
      if (id) {
        setShowCreateModal(false);
        setPendingSelectId(id);
        // 활성만 필터 해제 (draft는 비활성이므로 목록에 보이게)
        setFilterActiveOnly(false);
        await listEvaluationTemplates({ activeOnly: undefined });
      }
    } finally {
      setSaving(false);
    }
  }, [editTitle, editType, editMaxDepth, editScale, createEvaluationTemplate, listEvaluationTemplates, isSystemAdmin, editCompanyCode]);

  // ============================================
  // V57.3: pre-save 구조 검증
  // ============================================

  const validateTemplateStructure = useCallback((): string | null => {
    if (!editTitle.trim()) return '제목을 입력해주세요.';
    const topNodes = editNodes.filter(n => n.parentId === null && n.isActive);
    if (topNodes.length === 0) return '최소 1개 이상의 대분류가 필요합니다.';
    const ratingItems = editItems.filter(it => it.isActive && it.inputType === 'rating');
    if (ratingItems.length === 0) return '최소 1개 이상의 점수 평가 문항(rating)이 필요합니다.';
    return null;
  }, [editTitle, editNodes, editItems]);

  // ============================================
  // 저장 (인플레이스)
  // ============================================

  const handleSave = useCallback(async () => {
    if (!selectedTemplate) return;
    // V57.6: 활성 템플릿만 구조 검증. draft/ready(비활성)는 자유 저장.
    if (selectedTemplate.isActive) {
      const validationError = validateTemplateStructure();
      if (validationError) { alert(validationError); return; }
    } else if (!editTitle.trim()) {
      alert('제목을 입력해주세요.'); return;
    }
    setSaving(true);
    try {
      const ok = await updateEvaluationTemplate({
        templateId: selectedTemplate.id,
        title: editTitle.trim(),
        maxDepth: editMaxDepth,
        scoringScale: editScale,
        scoringMethod: 'weighted_sum',
        nodes: editNodes,
        items: editItems,
      });
      if (ok) {
        await listEvaluationTemplates({ activeOnly: filterActiveOnly || undefined });
        // V57.6: 로컬 상태도 갱신 (templateStatus가 BE에서 자동 계산됨)
        setSelectedTemplate(prev => prev ? { ...prev, title: editTitle.trim() } : null);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, editTitle, editMaxDepth, editScale, editNodes, editItems, updateEvaluationTemplate, listEvaluationTemplates, filterActiveOnly, validateTemplateStructure]);

  // ============================================
  // 새 버전으로 저장
  // ============================================

  const handleSaveAsNewVersion = useCallback(async () => {
    if (!selectedTemplate) return;
    if (!editTitle.trim()) { alert('제목을 입력해주세요.'); return; }
    setSaving(true);
    try {
      // V57.6: 새 버전도 draft로 생성. 항목 포함하여 복사됨.
      const id = await createEvaluationTemplate({
        companyCode: selectedTemplate.companyCode,
        type: selectedTemplate.type,
        title: editTitle.trim(),
        maxDepth: editMaxDepth,
        scoringScale: editScale,
        scoringMethod: 'weighted_sum',
        nodes: editNodes,
        items: editItems,
      });
      if (id) {
        setFilterActiveOnly(false);
        await listEvaluationTemplates({ activeOnly: undefined });
      }
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, editTitle, editMaxDepth, editScale, editNodes, editItems, createEvaluationTemplate, listEvaluationTemplates]);

  // ============================================
  // V57.5: 활성화/비활성화 토글
  // ============================================

  const handleActivate = useCallback(async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`"${selectedTemplate.title}" 템플릿을 활성화하시겠습니까?\n\n• 문항 구조가 검증됩니다\n• 같은 유형의 다른 활성 템플릿은 자동으로 비활성화됩니다`)) return;
    setSaving(true);
    try {
      const ok = await updateEvaluationTemplate({
        templateId: selectedTemplate.id,
        isActive: true,
      });
      if (ok) {
        await listEvaluationTemplates({
          activeOnly: filterActiveOnly || undefined,
          type: (filterType as EvaluationType) || undefined,
        });
        setSelectedTemplate(prev => prev ? { ...prev, isActive: true, templateStatus: 'ready' as TemplateStatus } : null);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, updateEvaluationTemplate, listEvaluationTemplates, filterActiveOnly, filterType]);

  const handleDeactivate = useCallback(async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`"${selectedTemplate.title}" 템플릿을 비활성화하시겠습니까?`)) return;
    setSaving(true);
    try {
      const ok = await updateEvaluationTemplate({
        templateId: selectedTemplate.id,
        isActive: false,
      });
      if (ok) {
        await listEvaluationTemplates({
          activeOnly: filterActiveOnly || undefined,
          type: (filterType as EvaluationType) || undefined,
        });
        setSelectedTemplate(prev => prev ? { ...prev, isActive: false } : null);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, updateEvaluationTemplate, listEvaluationTemplates, filterActiveOnly, filterType]);

  // ============================================
  // V57.7: 시스템 템플릿 시드
  // ============================================

  const handleSeedSystemTemplates = useCallback(async () => {
    if (!window.confirm('KR 시스템 템플릿(수습/근무평가)을 생성합니다.\n이미 존재하면 스킵됩니다.')) return;
    setSaving(true);
    try {
      const result = await seedSystemEvaluationTemplates('KR');
      if (result) {
        alert(result.message);
        await listEvaluationTemplates({ activeOnly: filterActiveOnly || undefined });
      }
    } finally {
      setSaving(false);
    }
  }, [seedSystemEvaluationTemplates, listEvaluationTemplates, filterActiveOnly]);

  // ============================================
  // 노드 조작
  // ============================================

  const nodesMap = useMemo(() => {
    const m = new Map<string, EvaluationNode>();
    for (const n of editNodes) m.set(n.id, n);
    return m;
  }, [editNodes]);

  const addNode = useCallback((parentId: string | null) => {
    if (parentId !== null) {
      const depth = getNodeDepth(parentId, nodesMap) + 1;
      if (depth >= editMaxDepth) return;
    }
    const siblings = editNodes.filter(n => n.parentId === parentId);
    const newNode: EvaluationNode = {
      id: generateId(),
      parentId,
      title: '새 분류',
      order: siblings.length,
      isActive: true,
    };
    setEditNodes(prev => [...prev, newNode]);
  }, [editNodes, editMaxDepth, nodesMap]);

  const removeNode = useCallback((nodeId: string) => {
    const descendants = new Set<string>();
    const collect = (id: string) => {
      descendants.add(id);
      editNodes.filter(n => n.parentId === id).forEach(n => collect(n.id));
    };
    collect(nodeId);
    setEditNodes(prev => prev.filter(n => !descendants.has(n.id)));
    setEditItems(prev => prev.filter(it => !descendants.has(it.nodeId)));
  }, [editNodes]);

  const updateNodeTitle = useCallback((nodeId: string, title: string) => {
    setEditNodes(prev => prev.map(n => n.id === nodeId ? { ...n, title } : n));
  }, []);

  const moveNode = useCallback((nodeId: string, direction: 'up' | 'down') => {
    setEditNodes(prev => {
      const node = prev.find(n => n.id === nodeId);
      if (!node) return prev;
      const siblings = prev.filter(n => n.parentId === node.parentId).sort((a, b) => a.order - b.order);
      const idx = siblings.findIndex(n => n.id === nodeId);
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === siblings.length - 1)) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      const updated = prev.map(n => {
        if (n.id === siblings[idx].id) return { ...n, order: siblings[swapIdx].order };
        if (n.id === siblings[swapIdx].id) return { ...n, order: siblings[idx].order };
        return n;
      });
      return updated;
    });
  }, []);

  // ============================================
  // 아이템 조작
  // ============================================

  const addItem = useCallback((nodeId: string) => {
    const existing = editItems.filter(it => it.nodeId === nodeId);
    const newItem: EvaluationItemV2 = {
      id: generateId(),
      nodeId,
      title: '새 문항',
      inputType: 'rating',
      weight: 1,
      requiredFor: ['self', 'manager'],
      order: existing.length,
      isActive: true,
    };
    setEditItems(prev => [...prev, newItem]);
  }, [editItems]);

  const removeItem = useCallback((itemId: string) => {
    setEditItems(prev => prev.filter(it => it.id !== itemId));
  }, []);

  const updateItem = useCallback((itemId: string, updates: Partial<EvaluationItemV2>) => {
    setEditItems(prev => prev.map(it => it.id === itemId ? { ...it, ...updates } : it));
  }, []);

  const moveItem = useCallback((itemId: string, direction: 'up' | 'down') => {
    setEditItems(prev => {
      const item = prev.find(it => it.id === itemId);
      if (!item) return prev;
      const siblings = prev.filter(it => it.nodeId === item.nodeId).sort((a, b) => a.order - b.order);
      const idx = siblings.findIndex(it => it.id === itemId);
      if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === siblings.length - 1)) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      return prev.map(it => {
        if (it.id === siblings[idx].id) return { ...it, order: siblings[swapIdx].order };
        if (it.id === siblings[swapIdx].id) return { ...it, order: siblings[idx].order };
        return it;
      });
    });
  }, []);

  // ============================================
  // 미리보기 합산
  // ============================================

  const [previewScores, setPreviewScores] = useState<Record<string, number>>({});

  const previewTotals = useMemo(() => {
    const topNodes = editNodes.filter(n => n.parentId === null && n.isActive);
    const byCategory: Record<string, { label: string; total: number; maxPossible: number }> = {};
    let grandTotal = 0;
    let grandMaxPossible = 0;

    const ratingItems = editItems.filter(it => it.isActive && it.inputType === 'rating');
    for (const item of ratingItems) {
      const topId = findTopAncestorId(item.nodeId, nodesMap);
      const topNode = nodesMap.get(topId);
      if (!topNode) continue;
      if (!byCategory[topId]) {
        byCategory[topId] = { label: topNode.title, total: 0, maxPossible: 0 };
      }
      const score = previewScores[item.id] || 0;
      byCategory[topId].total += score * item.weight;
      byCategory[topId].maxPossible += editScale.max * item.weight;
    }

    for (const cat of Object.values(byCategory)) {
      cat.total = Math.round(cat.total * 100) / 100;
      cat.maxPossible = Math.round(cat.maxPossible * 100) / 100;
      grandTotal += cat.total;
      grandMaxPossible += cat.maxPossible;
    }
    grandTotal = Math.round(grandTotal * 100) / 100;
    grandMaxPossible = Math.round(grandMaxPossible * 100) / 100;

    return { byCategory, grandTotal, grandMaxPossible };
  }, [editNodes, editItems, editScale, previewScores, nodesMap]);

  // ============================================
  // 렌더
  // ============================================

  // V57.3: 권한 없는 경우 접근 차단
  if (!canAccess) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>
        <AlertCircle size={48} style={{ margin: '0 auto 16px', display: 'block', color: '#EF4444' }} />
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>접근 권한이 없습니다</p>
        <p style={{ fontSize: 13, marginBottom: 24 }}>평가서 관리 페이지는 인사팀 담당자 또는 관리자만 접근 가능합니다.</p>
        <button onClick={() => navigate('/')} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#3B82F6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          홈으로
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>평가서 관리</h1>

      {error && (
        <div style={{ padding: '10px 16px', background: '#FEF2F2', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: '#DC2626' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* 필터 + 새 템플릿 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
        >
          <option value="">전체 유형</option>
          {Object.entries(EVALUATION_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={filterActiveOnly} onChange={e => setFilterActiveOnly(e.target.checked)} />
          활성만
        </label>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => {
            setShowCreateModal(true);
            setEditTitle('');
            setEditType('annual');
            setEditMaxDepth(2);
            setEditScale({ min: 1, max: 5, step: 1 });
            setEditNodes([]);
            setEditItems([]);
            setEditCompanyCode('');
            setSelectedTemplate(null);
          }}
          style={{ padding: '8px 16px', background: '#3B82F6', color: '#fff', borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
        >
          <Plus size={16} /> 새 템플릿
        </button>
        {/* V57.7: system_admin 전용 시드 버튼 */}
        {isSystemAdmin && (
          <button
            onClick={handleSeedSystemTemplates}
            disabled={saving}
            style={{ padding: '8px 16px', background: '#7C3AED', color: '#fff', borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, opacity: saving ? 0.6 : 1 }}
          >
            시스템 템플릿 시드 (KR)
          </button>
        )}
      </div>

      {/* 템플릿 목록 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>제목</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>유형</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>버전</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>상태</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>구조</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>수정일</th>
            </tr>
          </thead>
          <tbody>
            {filteredTemplates.map(tpl => (
              <tr
                key={tpl.id}
                onClick={() => handleSelectTemplate(tpl)}
                style={{
                  borderBottom: '1px solid #F3F4F6',
                  cursor: 'pointer',
                  background: selectedTemplate?.id === tpl.id ? '#EFF6FF' : undefined,
                }}
              >
                <td style={{ padding: '10px 12px' }}>{tpl.title}</td>
                <td style={{ padding: '10px 12px' }}>{EVALUATION_TYPE_LABELS[tpl.type] || tpl.type}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>v{tpl.version}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  {(() => {
                    // V57.6: tri-state 배지 (초안/준비/활성)
                    if (tpl.isActive) return (
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#DCFCE7', color: '#166534' }}>활성</span>
                    );
                    if (tpl.templateStatus === 'draft') return (
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FEF3C7', color: '#92400E' }}>초안</span>
                    );
                    return (
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#F3F4F6', color: '#6B7280' }}>준비</span>
                    );
                  })()}
                  {/* V57.7: 시스템 고정 배지 */}
                  {tpl.isSystemTemplate && (
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#DBEAFE', color: '#1E40AF', marginLeft: 4 }}>고정</span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, color: '#6B7280' }}>
                  {tpl.nodes?.length ? `V2 (${tpl.nodes.length}노드)` : 'V1'}
                </td>
                <td style={{ padding: '10px 12px', color: '#6B7280' }}>
                  {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleDateString('ko-KR') : '-'}
                </td>
              </tr>
            ))}
            {filteredTemplates.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>템플릿이 없습니다</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* 새 템플릿 생성 모달 */}
      {showCreateModal && (
        <ModalOverlay onClose={() => setShowCreateModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>새 V2 템플릿 생성</h2>
            {/* V57.6.1: system_admin 전용 회사 선택 */}
            {isSystemAdmin && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  회사 <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <select
                  value={editCompanyCode}
                  onChange={e => setEditCompanyCode(e.target.value as CompanyCode | '')}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as const,
                    border: !editCompanyCode ? '1px solid #F87171' : '1px solid #D1D5DB',
                  }}
                >
                  <option value="">회사 선택</option>
                  {COMPANY_CODE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {!editCompanyCode && (
                  <p style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>회사를 선택해주세요</p>
                )}
              </div>
            )}
            <TemplateInfoEditor
              title={editTitle} setTitle={setEditTitle}
              type={editType} setType={setEditType}
              maxDepth={editMaxDepth} setMaxDepth={setEditMaxDepth}
              scale={editScale} setScale={setEditScale}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowCreateModal(false)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer' }}>
                취소
              </button>
              <button onClick={handleCreate} disabled={saving || !editTitle.trim() || (isSystemAdmin && !editCompanyCode)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#3B82F6', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (saving || (isSystemAdmin && !editCompanyCode)) ? 0.6 : 1 }}>
                {saving ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* 편집 영역 */}
      {selectedTemplate && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
          {/* 탭 헤더 */}
          <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
            {(['info', 'editor', 'preview'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); if (tab === 'preview') setPreviewScores({}); }}
                style={{
                  padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: activeTab === tab ? '#fff' : 'transparent',
                  borderBottom: activeTab === tab ? '2px solid #3B82F6' : '2px solid transparent',
                  color: activeTab === tab ? '#3B82F6' : '#6B7280',
                }}
              >
                {tab === 'info' ? '기본정보' : tab === 'editor' ? '분류/문항 편집' : '미리보기'}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 8, padding: '6px 12px', alignItems: 'center' }}>
              {/* V57.7: 잠금 시 버튼 숨기고 안내 표시 */}
              {isTemplateLocked ? (
                <span style={{ fontSize: 12, color: '#6B7280', padding: '6px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                  시스템 고정 템플릿 (수정 불가)
                </span>
              ) : (
                <>
                  {/* V57.5: 활성화/비활성화 토글 */}
                  {selectedTemplate && !selectedTemplate.isActive && (
                    <button onClick={handleActivate} disabled={saving} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #059669', background: '#ECFDF5', color: '#059669', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                      활성화
                    </button>
                  )}
                  {selectedTemplate && selectedTemplate.isActive && (
                    <button onClick={handleDeactivate} disabled={saving} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #DC2626', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                      비활성화
                    </button>
                  )}
                  <div style={{ width: 1, height: 20, background: '#E5E7EB' }} />
                  <button onClick={handleSave} disabled={saving} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#3B82F6', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, opacity: saving ? 0.6 : 1 }}>
                    <Save size={14} /> 저장
                  </button>
                  <button onClick={handleSaveAsNewVersion} disabled={saving} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                    새 버전으로 저장
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ padding: 20 }}>
            {activeTab === 'info' && (
              <TemplateInfoEditor
                title={editTitle} setTitle={setEditTitle}
                type={editType} setType={setEditType}
                maxDepth={editMaxDepth} setMaxDepth={setEditMaxDepth}
                scale={editScale} setScale={setEditScale}
                readonly
              />
            )}

            {activeTab === 'editor' && (
              isTemplateLocked ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>
                  <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>시스템 고정 템플릿</p>
                  <p style={{ fontSize: 13 }}>이 템플릿은 수정할 수 없습니다. 미리보기 탭에서 구조를 확인하세요.</p>
                </div>
              ) : (
                <TreeEditor
                  nodes={editNodes}
                  items={editItems}
                  maxDepth={editMaxDepth}
                  scale={editScale}
                  nodesMap={nodesMap}
                  onAddNode={addNode}
                  onRemoveNode={removeNode}
                  onUpdateNodeTitle={updateNodeTitle}
                  onMoveNode={moveNode}
                  onAddItem={addItem}
                  onRemoveItem={removeItem}
                  onUpdateItem={updateItem}
                  onMoveItem={moveItem}
                />
              )
            )}

            {activeTab === 'preview' && (
              <PreviewTab
                nodes={editNodes}
                items={editItems}
                scale={editScale}
                nodesMap={nodesMap}
                scores={previewScores}
                setScores={setPreviewScores}
                totals={previewTotals}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 기본정보 편집기
// ============================================

function TemplateInfoEditor({ title, setTitle, type, setType, maxDepth, setMaxDepth, scale, setScale, readonly }: {
  title: string; setTitle: (v: string) => void;
  type: EvaluationType; setType: (v: EvaluationType) => void;
  maxDepth: 1 | 2 | 3; setMaxDepth: (v: 1 | 2 | 3) => void;
  scale: ScoringScale; setScale: (v: ScoringScale) => void;
  readonly?: boolean;
}) {
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, boxSizing: 'border-box' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={labelStyle}>제목</label>
        <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="평가서 제목" />
      </div>
      <div>
        <label style={labelStyle}>평가 유형</label>
        <select value={type} onChange={e => setType(e.target.value as EvaluationType)} style={inputStyle} disabled={readonly}>
          {Object.entries(EVALUATION_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={labelStyle}>분류 최대 깊이</label>
        <select value={maxDepth} onChange={e => setMaxDepth(Number(e.target.value) as 1 | 2 | 3)} style={inputStyle}>
          <option value={1}>1단계 (대분류만)</option>
          <option value={2}>2단계 (대/중분류)</option>
          <option value={3}>3단계 (대/중/소분류)</option>
        </select>
      </div>
      <div>
        <label style={labelStyle}>점수 최소</label>
        <input type="number" value={scale.min} onChange={e => setScale({ ...scale, min: Number(e.target.value) })} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>점수 최대</label>
        <input type="number" value={scale.max} onChange={e => setScale({ ...scale, max: Number(e.target.value) })} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>점수 단위 (ex 0.5, 1)</label>
        <input type="number" value={scale.step} onChange={e => setScale({ ...scale, step: Number(e.target.value) })} style={inputStyle} min={1} />
      </div>
    </div>
  );
}

// ============================================
// 트리 에디터
// ============================================

function TreeEditor({ nodes, items, maxDepth, scale, nodesMap, onAddNode, onRemoveNode, onUpdateNodeTitle, onMoveNode, onAddItem, onRemoveItem, onUpdateItem, onMoveItem }: {
  nodes: EvaluationNode[];
  items: EvaluationItemV2[];
  maxDepth: 1 | 2 | 3;
  scale: ScoringScale;
  nodesMap: Map<string, EvaluationNode>;
  onAddNode: (parentId: string | null) => void;
  onRemoveNode: (nodeId: string) => void;
  onUpdateNodeTitle: (nodeId: string, title: string) => void;
  onMoveNode: (nodeId: string, direction: 'up' | 'down') => void;
  onAddItem: (nodeId: string) => void;
  onRemoveItem: (itemId: string) => void;
  onUpdateItem: (itemId: string, updates: Partial<EvaluationItemV2>) => void;
  onMoveItem: (itemId: string, direction: 'up' | 'down') => void;
}) {
  const renderNode = (node: EvaluationNode, depth: number) => {
    const children = getChildren(node.id, nodes);
    const nodeItems = getNodeItems(node.id, items);
    const canAddChild = depth + 1 < maxDepth;

    return (
      <div key={node.id} style={{ marginLeft: depth * 24 }}>
        {/* 노드 헤더 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 4,
          background: depth === 0 ? '#F0F9FF' : depth === 1 ? '#F9FAFB' : '#fff',
          borderRadius: 6, border: '1px solid #E5E7EB',
        }}>
          <span style={{ fontSize: 11, color: '#9CA3AF', minWidth: 20 }}>D{depth}</span>
          <input
            value={node.title}
            onChange={e => onUpdateNodeTitle(node.id, e.target.value)}
            style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #E5E7EB', fontSize: 13, fontWeight: depth === 0 ? 700 : 600 }}
          />
          <button onClick={() => onMoveNode(node.id, 'up')} style={iconBtnStyle} title="위로"><ArrowUp size={14} /></button>
          <button onClick={() => onMoveNode(node.id, 'down')} style={iconBtnStyle} title="아래로"><ArrowDown size={14} /></button>
          {canAddChild && (
            <button onClick={() => onAddNode(node.id)} style={{ ...iconBtnStyle, color: '#3B82F6' }} title="하위분류 추가">
              <Plus size={14} />
            </button>
          )}
          <button onClick={() => onAddItem(node.id)} style={{ ...iconBtnStyle, color: '#059669' }} title="문항 추가">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onRemoveNode(node.id)} style={{ ...iconBtnStyle, color: '#DC2626' }} title="삭제">
            <Trash2 size={14} />
          </button>
        </div>

        {/* 문항 */}
        {nodeItems.map(item => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', marginLeft: 24, marginBottom: 2,
            background: '#FFFBEB', borderRadius: 4, border: '1px solid #FDE68A', fontSize: 12,
          }}>
            <span style={{ color: '#92400E', fontSize: 10, minWidth: 24 }}>문항</span>
            <input
              value={item.title}
              onChange={e => onUpdateItem(item.id, { title: e.target.value })}
              style={{ flex: 1, padding: '3px 6px', borderRadius: 4, border: '1px solid #E5E7EB', fontSize: 12 }}
            />
            <select
              value={item.inputType}
              onChange={e => onUpdateItem(item.id, { inputType: e.target.value as 'rating' | 'text' | 'select' })}
              style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid #E5E7EB', fontSize: 11, width: 70 }}
            >
              <option value="rating">점수</option>
              <option value="text">텍스트</option>
              <option value="select">선택</option>
            </select>
            <input
              type="number"
              value={item.weight}
              onChange={e => onUpdateItem(item.id, { weight: Number(e.target.value) })}
              style={{ width: 50, padding: '3px 6px', borderRadius: 4, border: '1px solid #E5E7EB', fontSize: 11, textAlign: 'center' }}
              title="가중치"
              min={0}
              step={0.5}
            />
            <RoleCheckboxes
              value={item.requiredFor}
              onChange={requiredFor => onUpdateItem(item.id, { requiredFor })}
            />
            <button onClick={() => onMoveItem(item.id, 'up')} style={iconBtnStyle}><ArrowUp size={12} /></button>
            <button onClick={() => onMoveItem(item.id, 'down')} style={iconBtnStyle}><ArrowDown size={12} /></button>
            <button onClick={() => onRemoveItem(item.id)} style={{ ...iconBtnStyle, color: '#DC2626' }}><Trash2 size={12} /></button>
          </div>
        ))}

        {/* 자식 노드 */}
        {children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  const rootNodes = getChildren(null, nodes);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => onAddNode(null)}
          style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #3B82F6', background: '#EFF6FF', color: '#3B82F6', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <Plus size={14} /> 최상위 분류 추가
        </button>
      </div>

      {rootNodes.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
          분류를 추가해 주세요
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rootNodes.map(node => renderNode(node, 0))}
        </div>
      )}
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 4, color: '#6B7280', display: 'flex', alignItems: 'center',
};

// ============================================
// requiredFor 체크박스
// ============================================

function RoleCheckboxes({ value, onChange }: { value: EvaluationRole[]; onChange: (v: EvaluationRole[]) => void }) {
  const toggle = (role: EvaluationRole) => {
    if (value.includes(role)) {
      onChange(value.filter(r => r !== role));
    } else {
      onChange([...value, role]);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {(['self', 'lead', 'manager'] as const).map(role => (
        <button
          key={role}
          onClick={() => toggle(role)}
          style={{
            padding: '1px 4px', borderRadius: 3, fontSize: 9, fontWeight: 600, cursor: 'pointer',
            border: value.includes(role) ? '1px solid #3B82F6' : '1px solid #D1D5DB',
            background: value.includes(role) ? '#DBEAFE' : '#F9FAFB',
            color: value.includes(role) ? '#1D4ED8' : '#9CA3AF',
          }}
        >
          {role === 'self' ? '본' : role === 'lead' ? '리' : '팀'}
        </button>
      ))}
    </div>
  );
}

// ============================================
// 미리보기 탭
// ============================================

function PreviewTab({ nodes, items, scale, nodesMap, scores, setScores, totals }: {
  nodes: EvaluationNode[];
  items: EvaluationItemV2[];
  scale: ScoringScale;
  nodesMap: Map<string, EvaluationNode>;
  scores: Record<string, number>;
  setScores: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  totals: { byCategory: Record<string, { label: string; total: number; maxPossible: number }>; grandTotal: number; grandMaxPossible: number };
}) {
  const scaleButtons = useMemo(() => {
    const buttons: number[] = [];
    for (let i = scale.min; i <= scale.max; i += scale.step) buttons.push(i);
    return buttons;
  }, [scale]);

  const renderNodePreview = (node: EvaluationNode, depth: number) => {
    const children = getChildren(node.id, nodes).filter(n => n.isActive);
    const nodeItems = getNodeItems(node.id, items).filter(it => it.isActive);

    return (
      <div key={node.id} style={{ marginBottom: depth === 0 ? 16 : 8 }}>
        <div style={{
          fontSize: depth === 0 ? 15 : 13,
          fontWeight: depth === 0 ? 700 : 600,
          color: depth === 0 ? '#1F2937' : '#374151',
          padding: depth === 0 ? '8px 0' : '4px 0',
          borderBottom: depth === 0 ? '2px solid #E5E7EB' : undefined,
          marginLeft: depth > 0 ? (depth - 1) * 16 : 0,
        }}>
          {node.title}
        </div>

        {nodeItems.map(item => (
          <div key={item.id} style={{ padding: '8px 0 8px 16px', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 13, marginBottom: 4, color: '#374151' }}>
              {item.title}
              {item.weight !== 1 && <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 6 }}>x{item.weight}</span>}
            </div>
            {item.inputType === 'rating' ? (
              <div style={{ display: 'flex', gap: 6 }}>
                {scaleButtons.map(n => (
                  <button
                    key={n}
                    onClick={() => setScores(prev => ({ ...prev, [item.id]: n }))}
                    style={{
                      width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
                      border: scores[item.id] === n ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                      background: scores[item.id] === n ? '#3B82F6' : '#fff',
                      color: scores[item.id] === n ? '#fff' : '#374151',
                      fontWeight: 600, fontSize: 13,
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : item.inputType === 'text' ? (
              <textarea rows={2} style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} placeholder="텍스트 입력" />
            ) : (
              <select style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}>
                <option value="">선택...</option>
                {(item.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
          </div>
        ))}

        {children.map(child => renderNodePreview(child, depth + 1))}
      </div>
    );
  };

  const rootNodes = getChildren(null, nodes).filter(n => n.isActive);

  return (
    <div>
      <div style={{ padding: '8px 16px', background: '#FEF3C7', borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#92400E' }}>
        미리보기 모드 — 실제 저장되지 않습니다. 점수를 클릭하면 합산이 실시간으로 계산됩니다.
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* 폼 영역 */}
        <div style={{ flex: 1 }}>
          {rootNodes.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>분류/문항을 먼저 추가해 주세요</div>
          ) : rootNodes.map(node => renderNodePreview(node, 0))}
        </div>

        {/* 합산 사이드바 */}
        <div style={{ width: 240, flexShrink: 0 }}>
          <div style={{ position: 'sticky', top: 20, padding: 16, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>합산 결과</div>
            {Object.values(totals.byCategory).map(cat => (
              <div key={cat.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                <span style={{ color: '#374151' }}>{cat.label}</span>
                <span style={{ fontWeight: 600 }}>{cat.total.toFixed(1)} / {cat.maxPossible.toFixed(1)}</span>
              </div>
            ))}
            <div style={{ borderTop: '2px solid #E5E7EB', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
              <span>전체</span>
              <span style={{ color: '#3B82F6' }}>{totals.grandTotal.toFixed(1)} / {totals.grandMaxPossible.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
